import React, { useState } from 'react';
import { IconButton, IconButtonProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import { colors } from '../../theme/theme';
import EventToast from './EventToast';

interface CopyButtonProps extends Omit<IconButtonProps, 'onClick'> {
  /** The text value to copy to clipboard */
  valueToCopy: string;
  /** Optional custom icon to display in button and toast */
  icon?: React.ReactNode;
  /** Optional custom aria-label (defaults to "Copy to clipboard") */
  'aria-label'?: string;
}

const StyledCopyButton = styled(IconButton)(({ theme }) => ({
  padding: '8px',
  marginLeft: '8px',
  borderRadius: '50%',

  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.1)'
        : theme.palette.primary.light + '40',
  },

  // Fix: Handle both :focus and :focus-visible
  '&:focus': {
    outline: 'none',
  },

  '&:focus-visible': {
    outline: 'none',
  },

  '@media (min-width: 600px)': {
    display: 'inline-flex',
  },
}));

// Fallback copy method for non-secure contexts (HTTP on mobile)
const fallbackCopyToClipboard = (text: string): boolean => {
  const textarea = document.createElement('textarea');
  textarea.value = text;

  textarea.style.position = 'fixed';
  textarea.style.top = '0';
  textarea.style.left = '0';
  textarea.style.width = '2em';
  textarea.style.height = '2em';
  textarea.style.padding = '0';
  textarea.style.border = 'none';
  textarea.style.outline = 'none';
  textarea.style.boxShadow = 'none';
  textarea.style.background = 'transparent';
  textarea.style.opacity = '0';

  document.body.appendChild(textarea);

  textarea.focus();
  textarea.select();

  let successful = false;
  try {
    successful = document.execCommand('copy');
  } catch (err) {
    successful = false;
  }

  document.body.removeChild(textarea);
  return successful;
};

const CopyButton: React.FC<CopyButtonProps> = ({
  valueToCopy,
  icon,
  'aria-label': ariaLabel = 'Copy to clipboard',
  ...iconButtonProps
}) => {
  const [showToast, setShowToast] = useState(false);
  const [toastPosition, setToastPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);

  const handleCopyClick = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.stopPropagation();

    // CRITICAL: Capture button reference BEFORE async operations
    // React synthetic events are nullified after the handler completes
    const button = event.currentTarget as HTMLElement;
    const rect = button.getBoundingClientRect();
    const position = {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    };

    let copySuccessful = false;

    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(valueToCopy);
        copySuccessful = true;
      } else {
        copySuccessful = fallbackCopyToClipboard(valueToCopy);
      }
    } catch (err) {
      copySuccessful = fallbackCopyToClipboard(valueToCopy);
    }

    if (copySuccessful) {
      setToastPosition(position);
      setShowToast(true);
    }
  };

  const defaultIcon = <ContentCopyIcon fontSize="small" />;
  const displayIcon = icon || defaultIcon;

  return (
    <>
      <StyledCopyButton
        onClick={handleCopyClick}
        aria-label={ariaLabel}
        {...iconButtonProps}
      >
        {displayIcon}
      </StyledCopyButton>

      <EventToast
        show={showToast}
        position={toastPosition}
        icon={displayIcon}
        onClose={() => setShowToast(false)}
      />
    </>
  );
};

export default CopyButton;
